import { NgModule } from '@angular/core';
import { CommonModule, CurrencyPipe, DecimalPipe, DatePipe, PercentPipe, TitleCasePipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { PasswordPromptComponent } from './components/password-prompt/password-prompt.component';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { TranslocoModule } from '@ngneat/transloco';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FileUploadComponent } from './components/file-upload/file-upload.component';
import { DragAndDropDirective } from './components/file-upload/drag-and-drop.directive';
import { ValueWithGraphComponent } from './components/value-with-graph/value-with-graph.component';
import { NgApexchartsModule } from 'ng-apexcharts';
import { CalendarRangePanelComponent } from './components/calendar-range-panel/calendar-range-panel.component';
import { CalendarHeaderComponent } from './components/calendar-header/calendar-header.component';
import { MatCardModule } from '@angular/material/card';
import { PieChartComponent } from './components/pie-chart/pie-chart.component';
import { TopListComponent } from './components/top-list/top-list.component';
import { AutofocusDirective, CountryCodePipe, DebounceClickDirective, DurationPipe, SafePipe, StoresPipe, SubscriptionDurationPipe } from './shared.directives';
import { HomeRedirectComponent } from './components/home-redirect/home-redirect.component';
import { ServicesModalComponent } from './components/services-modal/services-modal.component';
import { InlineSVGModule } from 'ng-inline-svg';
import { MatSelectModule } from '@angular/material/select';
import { RouterModule } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { NgxPayPalModule } from 'ngx-paypal';
import { NgxStripeModule } from 'ngx-stripe';
import { CheckboxListComponent } from './components/checkbox-list/checkbox-list.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CardWithPieChartComponent } from './components/card-with-pie-chart/card-with-pie-chart.component';
import { PendingChangesGuard } from 'app/core/auth/guards/pendingChanges.guard';
import { FormFieldTooltipComponent } from './components/form-field-tooltip/form-field-tooltip.component';
import { StatusPillComponent } from './components/status-pill/status-pill.component';
import { MatMenuModule } from '@angular/material/menu';
import { NewArtistModalComponent } from './components/new-artist-modal/new-artist-modal';
import { FuseAlertComponent } from '@fuse/components/alert';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { FilterBarComponent } from './components/filter-bar/filter-bar.component';
import { MatSortModule } from '@angular/material/sort';
import { NewReleaseFormComponent } from 'app/shared/components/new-release-form/new-release-form.component';
import { CountrySelectorComponent } from 'app/shared/components/country-selector/country-selector.component';
import { TrackDetailsComponent } from 'app/shared/components/track-details/track-details.component';
import { MatChipsModule } from '@angular/material/chips';
import { MatRadioModule } from '@angular/material/radio';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MatStepperModule } from '@angular/material/stepper';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ReleaseSummaryModal } from 'app/shared/components/release-summary-modal/release-summary-modal.component';
import { ReleaseSummaryComponent } from 'app/shared/components/release-summary/release-summary.component';
import { ScriptService } from 'app/shared/script.service';
import { MonthRangePickerComponent } from 'app/shared/components/month-range-picker/month-range-picker.component';
import { TimeagoCustomFormatter, TimeagoFormatter, TimeagoIntl, TimeagoModule } from 'ngx-timeago';

@NgModule({
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatIconModule,
        MatButtonModule,
        TranslocoModule,
        MatFormFieldModule,
        MatInputModule,
        NgApexchartsModule,
        MatCardModule,
        InlineSVGModule,
        MatSelectModule,
        RouterModule,
        MatTooltipModule,
        MatProgressBarModule,
        NgxPayPalModule,
        MatCheckboxModule,
        MatMenuModule,
        NgxStripeModule.forChild(),
        FuseAlertComponent,
        DatePipe,
        TitleCasePipe,
        PercentPipe,
        MatAutocompleteModule,
        MatSortModule,
        MatChipsModule,
        MatRadioModule,
        DragDropModule,
        MatStepperModule,
        MatExpansionModule,
        MatProgressSpinnerModule,
    ],
    exports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        FileUploadComponent,
        DragAndDropDirective,
        TranslocoModule,
        PasswordPromptComponent,
        ValueWithGraphComponent,
        CalendarHeaderComponent,
        CalendarRangePanelComponent,
        MatCardModule,
        TopListComponent,
        PieChartComponent,
        SafePipe,
        StoresPipe,
        DatePipe,
        TitleCasePipe,
        PercentPipe,
        CountryCodePipe,
        ServicesModalComponent,
        SubscriptionDurationPipe,
        DurationPipe,
        AutofocusDirective,
        DebounceClickDirective,
        NgxPayPalModule,
        MatProgressBarModule,
        CheckboxListComponent,
        CardWithPieChartComponent,
        FormFieldTooltipComponent,
        StatusPillComponent,
        NewArtistModalComponent,
        MatMenuModule,
        FilterBarComponent,
        MatSortModule,
        NewReleaseFormComponent,
        CountrySelectorComponent,
        TrackDetailsComponent,
        ReleaseSummaryModal,
        ReleaseSummaryComponent,
        MonthRangePickerComponent,
        TimeagoModule,
        FuseAlertComponent,
    ],
    declarations: [
        PasswordPromptComponent,
        FileUploadComponent,
        ValueWithGraphComponent,
        DragAndDropDirective,
        CalendarRangePanelComponent,
        CalendarHeaderComponent,
        PieChartComponent,
        TopListComponent,
        SafePipe,
        StoresPipe,
        CountryCodePipe,
        HomeRedirectComponent,
        ServicesModalComponent,
        SubscriptionDurationPipe,
        DurationPipe,
        AutofocusDirective,
        DebounceClickDirective,
        CheckboxListComponent,
        CardWithPieChartComponent,
        FormFieldTooltipComponent,
        StatusPillComponent,
        NewArtistModalComponent,
        FilterBarComponent,
        NewReleaseFormComponent,
        CountrySelectorComponent,
        TrackDetailsComponent,
        ReleaseSummaryModal,
        ReleaseSummaryComponent,
        MonthRangePickerComponent,
    ],
    providers: [
        CurrencyPipe,
        DecimalPipe,
        DatePipe,
        TitleCasePipe,
        PendingChangesGuard,
        PercentPipe,
        CountryCodePipe,
        DurationPipe,
        ScriptService,
    ]
})
export class SharedModule {
}
